import React, { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { fmtEUR, fmtDateTimeIT } from "@/lib/format";
import { Plus, Trash2, Loader2, Save, Pencil, Crown, Shield, X, KeyRound, Trophy, AlertTriangle, Camera } from "lucide-react";
import { toast } from "sonner";
import { fileToAvatarDataUrl } from "@/lib/avatar";

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats")]);
      setItems(u.data.items || []);
      setStats(s.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const create = async (p) => {
    if (!p.email || !p.password || !p.name) { toast.error("Tutti i campi obbligatori"); return; }
    try { await api.post("/admin/users", p); toast.success("Utente creato"); setCreating(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const patch = async (uid, p) => {
    try { await api.patch(`/admin/users/${uid}`, p); toast.success("Aggiornato"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const uploadAvatar = async (uid, file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToAvatarDataUrl(file);
      await api.post(`/team/users/${uid}/avatar`, { image: dataUrl });
      toast.success("Foto profilo aggiornata");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore caricamento foto"); }
  };
  const del = async (uid) => {
    if (!window.confirm("Eliminare l'utente? I suoi dati restano nel DB ma non potrà più accedere.")) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <AppLayout
      title="Gestione Team"
      subtitle="Riservato amministratori"
      actions={
        <button onClick={() => setCreating({ email: "", password: "", name: "", role: "user" })} data-testid="btn-new-user" className="text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
          <Plus className="w-4 h-4" /> Nuovo utente
        </button>
      }
    >
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <Stat label="Utenti" value={stats.totals?.users ?? 0} />
          <Stat label="Clienti totali" value={stats.totals?.clients ?? 0} />
          <Stat label="Pratiche totali" value={stats.totals?.practices ?? 0} />
          <Stat label="Mese corrente" value={stats.month || "—"} />
        </div>
      )}

      {stats?.leaderboardCurrentMonth?.length > 0 && (
        <div className="mb-5 bg-white p-6" style={{ border: "1px solid #E8ECF2", borderRadius: 4, boxShadow: "0 1px 2px rgba(10,25,47,0.04)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-[#B7955B]" />
            <h3 className="font-serif text-base text-slate-900">Leaderboard Provvigioni — {stats.month}</h3>
          </div>
          <ol className="space-y-2">
            {stats.leaderboardCurrentMonth.map((row, i) => (
              <li key={row.userId} className="flex items-center gap-3 py-1.5 border-b border-[#F0F2F5] last:border-0">
                <span className="w-6 text-center font-serif text-lg text-[#8a6e3d]">{i + 1}</span>
                <span className="flex-1 text-slate-900 font-medium">{row.name}</span>
                <span className="text-xs text-slate-500 tabular-nums">{row.count} pratiche</span>
                <span className="font-medium text-slate-900 tabular-nums w-32 text-right">{fmtEUR(row.provvigioni)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] overflow-hidden">
        {creating && <UserEditor u={creating} isNew onChange={setCreating} onSave={() => create(creating)} onCancel={() => setCreating(null)} />}

        {loading ? <div className="py-10 text-center inline text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.1em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="text-left font-semibold py-2.5 px-4">Nome</th>
                <th className="text-left font-semibold py-2.5 px-4">Email</th>
                <th className="text-left font-semibold py-2.5 px-4">Ruolo</th>
                <th className="text-right font-semibold py-2.5 px-4">Clienti</th>
                <th className="text-right font-semibold py-2.5 px-4">Pratiche</th>
                <th className="text-left font-semibold py-2.5 px-4">Creato</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => editing?.id === u.id
                ? <tr key={u.id}><td colSpan={7} className="p-2 bg-amber-50/40"><UserEditor u={editing} onChange={setEditing} onSave={() => patch(u.id, editing)} onCancel={() => setEditing(null)} /></td></tr>
                : (
                  <tr key={u.id} className="border-b border-[#F0F2F5] tbl-row" data-testid={`u-${u.id}`}>
                    <td className="py-2.5 px-4 font-medium text-slate-900 flex items-center gap-2">
                      <label className="relative cursor-pointer group shrink-0" title="Carica foto profilo" data-testid={`u-avatar-${u.id}`}>
                        {u.avatar ? (
                          <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                        ) : (
                          <span className="w-8 h-8 rounded-full bg-[#0A192F] text-[#B7955B] text-[11px] font-bold flex items-center justify-center">
                            {(u.name || u.email || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("")}
                          </span>
                        )}
                        <span className="absolute inset-0 rounded-full bg-black/45 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <Camera className="w-3.5 h-3.5 text-white" />
                        </span>
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => { uploadAvatar(u.id, e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                      {u.role === "admin" ? <Crown className="text-[#B7955B] text-slate-400 w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                      {u.name}
                    </td>
                    <td className="py-2.5 px-4 text-slate-700 font-mono text-xs">{u.email}</td>
                    <td className="py-2.5 px-4">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-[3px] border ${u.role === "admin" ? "bg-[#F5EFE3] text-[#8a6e3d] border-[#E5D7B6]" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {u.role || "user"}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums">{u.counts?.clients ?? 0}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums">{u.counts?.practices ?? 0}</td>
                    <td className="py-2.5 px-4 text-xs text-slate-500 tabular-nums">{fmtDateTimeIT(u.created_at)}</td>
                    <td className="py-2.5 px-4 flex items-center gap-1">
                      <button onClick={() => setEditing({ ...u, password: "" })} className="p-1.5 hover:bg-slate-100 rounded-[3px] text-slate-600"><Pencil className="w-3.5 h-3.5" /></button>
                      {u.id !== user?.id && <button onClick={() => del(u.id)} className="p-1.5 hover:bg-rose-50 rounded-[3px] text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* DANGER ZONE — operazioni distruttive */}
      <DangerZone user={user} onReload={load} />
    </AppLayout>
  );
}

// ============================================================================
// DANGER ZONE — wipe all clients
// ============================================================================
function DangerZone({ user, onReload }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const expected = `DELETE_ALL_CLIENTS_${user?.email || ""}`;

  const doWipe = async () => {
    toast.error("Funzione disabilitata per sicurezza (Fase 0A)");
    return;
    // eslint-disable-next-line no-unreachable
    if (confirmText !== expected) {
      toast.error("Frase di conferma non corretta");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-all-clients", { confirm: confirmText });
      toast.success(`Cancellati ${r.data?.deleted_count || 0} clienti. Operazione completata.`);
      setOpen(false);
      setConfirmText("");
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione fallita");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-10 bg-white border-2 border-rose-200 rounded-[4px] p-5" data-testid="admin-danger-zone">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-rose-600" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg text-rose-900">Danger Zone</h3>
          <p className="text-[12px] text-slate-600 mt-0.5">
            Operazioni IRREVERSIBILI. Cancellano permanentemente i dati. Eseguibili solo da admin.
          </p>
        </div>
      </div>

      <div className="border border-rose-200 rounded-sm p-4 bg-rose-50/40">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 max-w-xl">
            <h4 className="font-semibold text-rose-900 text-sm">Cancella TUTTE le anagrafiche cliente</h4>
            <p className="text-[12px] text-rose-800 mt-1 leading-snug">
              Hard delete di tutti i clienti del database. Sparire per sempre, senza recupero possibile.
              <br />
              <b>Cosa NON viene toccato</b>: pratiche del mese, reminders/agenda, vault password, custom fields,
              board, knowledge base, voice notes, audit log.
            </p>
          </div>
          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="wipe-clients-open"
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-sm flex items-center gap-1.5 shrink-0"
            >
              <Trash2 className="w-4 h-4" /> Cancella tutti i clienti
            </button>
          ) : null}
        </div>

        {open && (
          <div className="mt-4 p-3 bg-white border border-rose-300 rounded-sm" data-testid="wipe-clients-form">
            <p className="text-[12.5px] text-rose-900 mb-2">
              ⚠️ Per confermare, scrivi <b>esattamente</b> la frase qui sotto:
            </p>
            <code className="block bg-slate-50 border border-slate-200 px-3 py-2 text-[11.5px] font-mono select-all">
              {expected}
            </code>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Incolla qui la frase sopra…"
              data-testid="wipe-clients-confirm"
              className="w-full mt-2 px-3 py-2 text-sm border border-rose-300 rounded-sm font-mono focus:outline-none focus:border-rose-600"
              autoComplete="off"
            />
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={doWipe}
                disabled={busy || confirmText !== expected}
                data-testid="wipe-clients-confirm-btn"
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-sm flex items-center gap-1.5"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Cancella ora
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); setConfirmText(""); }}
                disabled={busy}
                data-testid="wipe-clients-cancel"
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm rounded-sm"
              >
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-[3px] p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-serif text-2xl mt-1 tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function UserEditor({ u, isNew, onChange, onSave, onCancel }) {
  const set = (k, v) => onChange({ ...u, [k]: v });
  return (
    <div className="border border-amber-300 bg-amber-50/40 rounded-[3px] p-4 m-2">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Nome</label>
          <input value={u.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="u-name" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Email {!isNew && "(non modificabile)"}</label>
          <input value={u.email || ""} disabled={!isNew} onChange={(e) => set("email", e.target.value)} data-testid="u-email" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] font-mono disabled:bg-slate-100 disabled:text-slate-500" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Ruolo</label>
          <select value={u.role || "user"} onChange={(e) => set("role", e.target.value)} data-testid="u-role" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]">
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Password {!isNew && "(lascia vuoto per non cambiare)"}</label>
          <input type="password" value={u.password || ""} onChange={(e) => set("password", e.target.value)} data-testid="u-password" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]" placeholder={isNew ? "min. 6 caratteri" : ""} />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-slate-300 rounded-[3px] hover:bg-white text-slate-700 inline mr-1"><X className="w-4 h-4" /> Annulla</button>
        <button onClick={onSave} data-testid="u-save" className="px-3 py-1.5 text-sm text-white rounded-[3px] flex items-center gap-1.5" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} ><Save className="w-4 h-4" /> Salva</button>
      </div>
    </div>
  );
}
